import React from 'react';
import { Box, Typography } from '@mui/material';
import { apiDocCodeBg, apiDocCodeText, gray40, gray80, gray100 } from '../../../constants/colors';
import { regularMd, regularSm, regularXs, sectionTitle } from '../../../constants/location';
import { apiKeyPrefix } from '../../../constants/enum';

interface Props {
  environmentHost: string;
}

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  intro: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  pageTitle: {
    ...sectionTitle,
  },
  introText: {
    ...regularSm,
    color: gray80,
  },
  step: {
    border: `1px solid ${gray40}`,
    borderRadius: '4px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  stepTitle: {
    color: gray100,
    fontSize: '18px',
    fontWeight: 500,
    lineHeight: 'normal',
  },
  stepText: {
    ...regularSm,
    color: gray100,
  },
  codeBox: {
    backgroundColor: apiDocCodeBg,
    color: apiDocCodeText,
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '13px',
    padding: '16px',
    overflowX: 'auto',
    whiteSpace: 'pre',
  },
  codeCaption: {
    ...regularXs,
    color: gray80,
  },
  appendix: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  appendixTitle: {
    ...regularMd,
    color: gray100,
    fontWeight: 500,
  },
  listItem: {
    ...regularSm,
    color: gray100,
  },
  listItemDescription: {
    ...regularXs,
    color: gray80,
    marginTop: '4px',
  },
};

const QuickStartGuide: React.FC<Props> = ({ environmentHost }) => {
  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.intro}>
        <Typography sx={styles.pageTitle}>クイックスタート</Typography>
        <Typography sx={styles.introText}>
          5 ステップで Gen Drive API の動作確認ができます。
        </Typography>
      </Box>

      <Box sx={styles.step}>
        <Typography sx={styles.stepTitle}>1. APIキーを発行する</Typography>
        <Typography sx={styles.stepText}>
          「アカウントメニュー → APIキー管理」から発行します。発行直後の 1
          度だけ平文キーが表示されますので、安全な場所に保存してください。プレフィックス：本番は{' '}
          <code>{apiKeyPrefix.live}</code>、テスト用は <code>{apiKeyPrefix.test}</code> です。
        </Typography>
      </Box>

      <Box sx={styles.step}>
        <Typography sx={styles.stepTitle}>2. IP ホワイトリストを登録する（任意）</Typography>
        <Typography sx={styles.stepText}>
          発行したキーごとに CIDR 形式で許可 IP を登録できます。空にすると無制限ですが、
          本番運用では送信元 IP の絞り込みを推奨します。
        </Typography>
      </Box>

      <Box sx={styles.step}>
        <Typography sx={styles.stepTitle}>3. curl で送信する</Typography>
        <Typography sx={styles.stepText}>
          まずローカルでサンプル PDF を Base64 化し、<code>POST /v1/ocr</code> に送ります。
        </Typography>
        <Typography sx={styles.codeCaption}>Base64 ヘルパ (macOS / Linux):</Typography>
        <Box sx={styles.codeBox}>
          {`BASE64=$(base64 < invoice.pdf | tr -d '\\n')
curl -X POST '${environmentHost}/v1/ocr' \\
  -H 'Content-Type: application/json' \\
  -H "X-GenDrive-Api-Key: $GENDRIVE_API_KEY" \\
  -H 'Idempotency-Key: erp-invoice-12345' \\
  -d "{
    \\"pattern_id\\": \\"xxxxxxxxxxxxxxxxxxxx\\",
    \\"files\\": [
      {
        \\"file_name\\": \\"invoice.pdf\\",
        \\"mime_type\\": \\"application/pdf\\",
        \\"data\\": \\"$BASE64\\"
      }
    ]
  }"`}
        </Box>
        <Box sx={styles.appendix}>
          <Typography sx={styles.appendixTitle}>
            <code>pattern_id</code> の取得方法
          </Typography>
          <Box>
            <Typography sx={styles.listItem}>
              ・読取パターン一覧画面で対象のパターンを開く
            </Typography>
            <Typography sx={styles.listItemDescription}>
              URL の末尾（例: /ai-ocr/pattern/xxxxxxxxxxxxxxxxxxxx）が pattern_id です。
            </Typography>
          </Box>
          <Box>
            <Typography sx={styles.listItem}>
              ・<code>pattern_id</code> を省略した場合
            </Typography>
            <Typography sx={styles.listItemDescription}>
              自動仕分けキューに投入され、判別されたパターンで OCR が実行されます。
              判別できなかったファイルは未仕分けとしてエラー扱いになります。
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.step}>
        <Typography sx={styles.stepTitle}>4. 結果を確認する</Typography>
        <Typography sx={styles.stepText}>
          OCR は非同期で処理されます。処理が完了すると、レスポンスの <code>result_ids</code>{' '}
          に該当する OCR 結果が画面の「OCR結果一覧」に表示されます。
        </Typography>
      </Box>

      <Box sx={styles.step}>
        <Typography sx={styles.stepTitle}>5. Webhook を登録する（任意）</Typography>
        <Typography sx={styles.stepText}>
          OCR 完了通知を外部システムで受け取りたい場合は、読取パターンごとに Webhook
          を設定できます。読取パターン一覧で対象パターンの「︙」メニューを開き、「Webhook
          設定」から登録してください。なお、Webhook 機能のご利用には事務局での有効化が必要です。
        </Typography>
      </Box>
    </Box>
  );
};

export default QuickStartGuide;
